import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { CustomFilterField, TableModel, TableView } from 'src/core/ui/collections/table';
import { Button, Card, Divider, Modal, App, Row, Select, Space, Spin, Tooltip, theme, Col } from 'antd';
import { Query } from 'src/core/stores/data-store';
import { formatMessage } from 'src/core/services/http.service';
import {
  DeviceType,
  LicenseDataSummaryItem,
  LicenseDataSummaryStore,
  ReportItemStore,
  CsvReportRequest,
  getLicenseTableHeaders,
  dateDiffToHours,
  licenseStarted,
  licenseIsEnabled,
  isHourBasedLicense,
  licenseEnded,
  hourBasedLicenses,
  LicenseCreationAttributesStore,
  LicenseCreationAttributes,
  deviceTypeSorter,
  deviceTypeLabels,
  getLicenseToolCode,
  getSmartCoreLicenseToolDiameter,
} from '../../stores/licensing-store';
import NewLicenseDialog from '../licenses/license-new';
import ShowLicenseDialog from '../licenses/license-show';
import { DateRangeFilter } from '../../common/components/table-filters/date-range-filter';
import { isNullOrUndefined, isUndefined } from 'util';
import { isNullOrWhitespace } from '../../core/utils/object';
import { OptionsFilter } from '../../common/components/table-filters/options-filter';
import CsvReport from '../licenses/reports/csv-report';
import i18next, { Languages } from '../../i18n';
import moment from 'moment';
import useMediaQuery from 'src/common/hooks/use-media-query';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { ENV } from 'src/';
import { emptyTableModel, getCurrentOrg, getDateFormat } from 'src/common/utils';
import { useNavigate, Link } from 'react-router-dom';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { RangeValue } from 'src/core/ui/date-components/utils';
import { Country, IdentityType } from 'src/common/types';
import { OrganizationSummaryStore } from 'src/stores/organizations-store';
import DatePicker from 'src/core/ui/date-components/date-picker';
import { intersection, isEqual, parseInt } from 'lodash';
import { EMPTY_GUID } from 'src/common/types';
import { useIdentity } from 'src/core/services/authentication';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
const { useToken } = theme;

const { RangePicker } = DatePicker;

interface DateFilter {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

const LicenseListPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const config: AppConfiguration = useContext(AppConfig);
  const initialQuery = {
    searchQuery: '',
    orderBy: [{ field: 'CreatedOn', direction: 'Descending', useProfile: false }],
    filter: {},
    skip: 0,
    take: 10,
  } as Query;
  const [query, setQuery] = useState(initialQuery);

  const licenseDataStore = container.get(LicenseDataSummaryStore);
  const licenseDataState = licenseDataStore.state;

  const reportItemStore = container.get(ReportItemStore);

  const [newLicenseVisible, setNewLicenseVisible] = useState(false);
  const [licenseVisible, setLicenseVisible] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState('');
  const organizationsByCountryFilter = useRef<string[]>([]);
  const selectedOrganizationFilter = useRef<string>();

  const onMobile = useMediaQuery('(max-width: 768px)');

  setHeader(t('Licenses'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'licenses', breadcrumbName: t('Licenses') },
  ]);
  let getFilterStrFunc = (propName: string): string => {
    let filterStr = (query.filter as any)[propName] as string;
    if (isUndefined(filterStr) || isNullOrWhitespace(filterStr)) return '';
    const result = '[' + filterStr + ']';
    return result;
  };

  const createdOnFilter = (query.filter as any)['createdOn'];
  const hasCreatedOnFilter =
    !isNullOrUndefined(createdOnFilter) && (!isNullOrUndefined(createdOnFilter.ge) || !isNullOrUndefined(createdOnFilter.le));
  const fromDateFilter = (query.filter as any)['fromDate'];
  const hasFromDateFilter = !isNullOrUndefined(fromDateFilter) && (!isNullOrUndefined(fromDateFilter.ge) || !isNullOrUndefined(fromDateFilter.le));
  const untilDateFilter = (query.filter as any)['untilDate'];
  const hasUntilDateFilter =
    !isNullOrUndefined(untilDateFilter) && (!isNullOrUndefined(untilDateFilter.ge) || !isNullOrUndefined(untilDateFilter.le));

  const organizationNameHeader = t('Organization') + '   ' + getFilterStrFunc('OrganizationName');
  const toolCodeHeader = t('ToolCode') + '   ' + getFilterStrFunc('ToolCode');
  const customerHeader = t('Customer') + '   ' + getFilterStrFunc('Customer');
  const projectHeader = t('Project') + '   ' + getFilterStrFunc('Project');
  const createdOnHeader = t('CreatedOn') + ' ' + (hasCreatedOnFilter ? '[*]' : '');
  const fromDateHeader = t('FromDate') + ' ' + (hasFromDateFilter ? '[*]' : '');
  const untilDateHeader = t('UntilDate') + ' ' + (hasUntilDateFilter ? '[*]' : '');
  const deviceTypeHeader = t('DeviceType') + '   ' + getFilterStrFunc('DeviceType');
  const createdByHeader = t('CreatedBy') + '   ' + getFilterStrFunc('CreatedBy');
  const [selectFilterOpen, setSelectFilterOpen] = useState(false);
  const [selectedCustomDate, setSelectedCustomDate] = useState<RangeValue<moment.Moment>>();
  const [selectedDateRangeFilterValue, setSelectedDateRangeFilterValue] = useState<string | undefined>();
  const labelRef = useRef<string | undefined>();
  const activeDatesFilter = useRef<DateFilter>();
  const organizationsStore = container.get(OrganizationSummaryStore);
  const organizationsState = organizationsStore.state;
  const organizationIdsFilter = useRef<string[]>();
  const { notification } = App.useApp();
  const [filteredByCountry, setFilteredByCountry] = useState(false);
  const [filteredByOrganization, setFilteredByOrganization] = useState(false);

  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const licenseCreationAttributesState = licenseCreationAttributesStore.state;
  const identity = useIdentity();
  const currentOrg = getCurrentOrg(identity);
  const currentUser = identity.profile['sub'];
  const { token } = useToken();

  const getActivationStatusLabel = (license: LicenseDataSummaryItem) => {
    let color = '';
    let text = '';

    if (license.signOffPending) {
      color = token.colorWarning;
      text = t('ApprovalPending');
    } else if (licenseIsEnabled(license.fromDate, license.untilDate, license.deviceType)) {
      color = token.colorSuccess;
      text = t('Active');
    } else {
      color = token.colorError;
      text = t('NotActive');
    }

    return <span style={{ color }}>{text}</span>;
  };

  const [itemModal, setItemModal] = useState<CsvReportRequest>({
    headers: getLicenseTableHeaders(t),
    csvSeparator: ',',
    currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
    fileName: `global_licenses.csv`,
    yesTranslation: t('Yes'),
    noTranslation: t('No'),
    organizationCountryPairs: [],
    activeTranslation: t('Active'),
    notActiveTranslation: t('NotActive'),
  } as CsvReportRequest);

  const load = async () => {
    await organizationsStore.load({ take: 1000, skip: 0 });
    await licenseCreationAttributesStore.getByUserScope({ identityId: currentUser, scopeId: currentOrg.id, identityType: IdentityType.User });
    await licenseDataStore.loadFromOrganizationIds(query, organizationIdsFilter.current);
    organizationsByCountryFilter.current = organizationsState.items.value.map((v) => v.id);
  };

  useEffect(
    () => {
      load();
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setCustomFilter = (filter: RangeValue<moment.Moment>) => {
    const customLabel = `${filter![0]!.format(getDateFormat())} - ${filter![1]!.format(getDateFormat())}`;
    labelRef.current = customLabel;

    setSelectedCustomDate(filter);

    if (filter !== null) {
      onFilterChange(filter[0], filter[1]);
    } else {
      onFilterChange(null, null);
    }

    setSelectedDateRangeFilterValue(customLabel);
  };

  const onFilterChange = (startDate: moment.Moment | null, endDate: moment.Moment | null) => {
    if (!startDate || !endDate) {
      activeDatesFilter.current = undefined;
      setSelectedDateRangeFilterValue(undefined);
    } else {
      startDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.add(1, 'day');
      endDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      endDate.subtract(1, 'minute');
      activeDatesFilter.current = { startDate, endDate };
    }
    handleQueryChanged(query);
    setSelectFilterOpen(false);
  };

  const handleExportOk = async () => {
    let tempItemModal = {
      ...itemModal,
      headers: getLicenseTableHeaders(t),
      currentLanguage: i18next.language !== Languages.Russian ? i18next.language : 'ru-RU',
      fileName: `global_licenses.csv`,
      yesTranslation: t('Yes'),
      noTranslation: t('No'),
      activeTranslation: t('Active'),
      notActiveTranslation: t('NotActive'),
      organizationIds: organizationIdsFilter.current,
      organizationCountryPairs: organizationsState.items.value.map((v) => ({ country: t(v.country), organizationId: v.id })),
    };
    let response = await reportItemStore.getFull({ ...query, take: 1000000 }, tempItemModal);
    setReportsModal(false);
    if (!response) {
      notification.error({
        placement: 'bottomLeft',
        duration: 20,
        message: t('UnexpectedError'),
      });
    }
  };

  const handleExportCancel = () => {
    setReportsModal(false);
  };

  const onClearActiveRangeFilter = () => {
    onFilterChange(null, null);
    setSelectedDateRangeFilterValue(undefined);
    setSelectFilterOpen(false);
    activeDatesFilter.current = undefined;
    labelRef.current = undefined;
    handleQueryChanged(query);
    setSelectedCustomDate([null, null]);
  };

  const onChangeItem = (separator: string) => {
    let tempItemModal = itemModal;
    tempItemModal.csvSeparator = separator || tempItemModal.csvSeparator;
    setItemModal(tempItemModal);
  };

  const [reportsModal, setReportsModal] = useState(false);

  const getLicenseActiveHours = (fromDate: Date, untilDate: Date, deviceType: DeviceType) => {
    if (!licenseStarted(fromDate, deviceType)) return 0;
    if (licenseEnded(untilDate, deviceType)) return Math.round(dateDiffToHours(fromDate, untilDate));
    const now = moment(new Date());
    if (!isHourBasedLicense(deviceType)) now.add(now.utcOffset(), 'minutes');
    return Math.round(dateDiffToHours(fromDate, now.toDate()));
  };

  const handleQueryChanged = async (query: Query) => {
    let extraFilters: { status?: string; activeRange?: string } = {};

    const nowUTC = moment(new Date());
    nowUTC.add(nowUTC.utcOffset(), 'minutes');

    const hourBasedLicenseFilter = hourBasedLicenses.map((v) => `deviceType eq '${v}'`).join(' or ');
    const dateBasedLicenseFilter = hourBasedLicenses.map((v) => `deviceType ne '${v}'`).join(' and ');

    // handle date range filter
    if (activeDatesFilter.current?.startDate && activeDatesFilter.current?.endDate) {
      const from = moment(activeDatesFilter.current.startDate);
      const fromUTC = moment(activeDatesFilter.current.startDate);
      fromUTC.add(fromUTC.utcOffset(), 'minutes');

      const fromIsoString = from.toISOString();
      const fromUtcIsoString = fromUTC.toISOString();

      const until = moment(activeDatesFilter.current.endDate);
      const untilUTC = moment(activeDatesFilter.current.endDate);
      untilUTC.add(untilUTC.utcOffset(), 'minutes');

      const untilIsoString = until.toISOString();
      const untilUtcIsoString = untilUTC.toISOString();

      const hourBasedLicensesRangeFilter = `(((FromDate ge ${fromIsoString} and FromDate le ${untilIsoString}) or (UntilDate ge ${fromIsoString} and UntilDate le ${untilIsoString}) or (FromDate le ${fromIsoString} and UntilDate ge ${untilIsoString})) and (${hourBasedLicenseFilter}))`;
      const dateBasedLicensesRangeFilter = `(((FromDate ge ${fromUtcIsoString} and FromDate le ${untilUtcIsoString}) or (UntilDate ge ${fromUtcIsoString} and UntilDate le ${untilUtcIsoString}) or (FromDate le ${fromUtcIsoString} and UntilDate ge ${untilUtcIsoString})) and (${dateBasedLicenseFilter}))`;

      extraFilters.activeRange = `(${hourBasedLicensesRangeFilter} or ${dateBasedLicensesRangeFilter})`;
    } else {
      delete extraFilters.activeRange;
    }

    //parse extra filters
    const filters = Object.values(extraFilters);

    if (filters.length === 0) {
      if ((query.filter as any).and) delete (query.filter as any).and;
    } else {
      (query.filter as any)['and'] = [filters.join(' and ')];
    }

    setQuery(query);
    await licenseDataStore.loadFromOrganizationIds(query, organizationIdsFilter.current);
  };

  const statusFilter = (value: string) => {
    const nowUTC = moment(new Date());
    const now = moment(new Date());
    nowUTC.add(nowUTC.utcOffset(), 'minutes');

    const hourBasedLicenseFilter = hourBasedLicenses.map((v) => `deviceType eq '${v}'`).join(' or ');
    const dateBasedLicenseFilter = hourBasedLicenses.map((v) => `deviceType ne '${v}'`).join(' and ');
    switch (value) {
      case 'Active':
        const activeDateFilter = `((fromDate le ${nowUTC.toISOString()} and untilDate ge ${nowUTC.toISOString()} and (${dateBasedLicenseFilter})) or (fromDate le ${now.toISOString()} and untilDate ge ${now.toISOString()} and (${hourBasedLicenseFilter})))`;
        return `(${activeDateFilter} and (signOffPending eq false))`;
      case 'NotActive':
        const notActiveDateFilter = `(((fromDate ge ${nowUTC.toISOString()} or untilDate le ${nowUTC.toISOString()}) and (${dateBasedLicenseFilter})) or ((fromDate ge ${now.toISOString()} or untilDate le ${now.toISOString()}) and (${hourBasedLicenseFilter})))`;
        return `(${notActiveDateFilter} and (signOffPending eq false))`;
      case 'Pending':
        return `(signOffPending eq true)`;
      case undefined:
        return undefined;
    }
  };

  const oganizationCountryFilter = (country: string | undefined) => {
    setFilteredByCountry(country !== undefined);
    if (country) {
      organizationsByCountryFilter.current = organizationsState.items.filter((v) => v.country.value === country).map((v) => v.id.value);
    } else {
      organizationsByCountryFilter.current = organizationsState.items.value.map((v) => v.id);
    }
    updateOrganizationsFilter();
  };

  const organizationFilter = (organizationId: string | undefined) => {
    selectedOrganizationFilter.current = organizationId;
    updateOrganizationsFilter();
    setFilteredByOrganization(organizationId !== undefined);
  };

  const updateOrganizationsFilter = () => {
    if (selectedOrganizationFilter.current && organizationsByCountryFilter.current.length !== organizationsState.items.length) {
      organizationIdsFilter.current = intersection([selectedOrganizationFilter.current], organizationsByCountryFilter.current);
      if (organizationIdsFilter.current?.length === 0) organizationIdsFilter.current = [EMPTY_GUID];
    } else if (selectedOrganizationFilter.current) {
      organizationIdsFilter.current = [selectedOrganizationFilter.current];
    } else if (organizationsByCountryFilter.current.length !== organizationsState.items.length) {
      organizationIdsFilter.current = organizationsByCountryFilter.current;
    } else {
      organizationIdsFilter.current = [];
    }
  };

  const tableModel = {
    query,
    columns: [
      {
        sortable: true,
        field: 'toolCode',
        title: toolCodeHeader,
        fixed: 'left',
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={licenseDataStore.baseUrl} fieldName={'toolCode'} fieldValue={'toolCode'} />,
        renderer: (data) => (
          <span>
            <Link to={`${data.id}`}>
              <span>{getLicenseToolCode(data)}</span>
            </Link>
          </span>
        ),
      },
      {
        sortable: false,
        width: '80px',
        fixed: 'none',
        field: 'toolDiameter',
        title: t('ToolDiameter'),
        renderer: (data) => <span>{data.deviceType === DeviceType.SCTD ? getSmartCoreLicenseToolDiameter(data) : ''}</span>,
      },
      {
        sortable: true,
        filter: (
          <OptionsFilter
            value={(query.filter as any)?.['deviceType']}
            options={Object.values(DeviceType)
              .sort(deviceTypeSorter)
              .map((y) => {
                return { label: [deviceTypeLabels[y]], value: y };
              })}
          />
        ),
        field: 'deviceType',
        fixed: 'none',
        title: deviceTypeHeader,
        renderer: (data) => <span>{deviceTypeLabels[data.deviceType]}</span>,
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        field: 'fromDate',
        fixed: 'none',
        title: fromDateHeader,
        renderer: (data) => (
          <span>{data.unlimited || isHourBasedLicense(data.deviceType) ? '-' : moment(data.fromDate).utc().format(getDateFormat())}</span>
        ),
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        field: 'untilDate',
        fixed: 'none',
        title: untilDateHeader,
        renderer: (data) => (
          <span>{data.unlimited || isHourBasedLicense(data.deviceType) ? '-' : moment(data.untilDate).utc().format(getDateFormat())}</span>
        ),
      },
      {
        sortable: false,
        field: 'workingHours',
        fixed: 'none',
        title: t('WorkingHours'),
        renderer: (data) => (
          <span style={{ marginRight: '60px' }}>
            {data.unlimited || !isHourBasedLicense(data.deviceType) ? '-' : parseInt(`${data.workingHours}`)}
          </span>
        ),
      },
      {
        sortable: false,
        field: 'hoursUsed',
        fixed: 'none',
        title: t('ActiveDays'),
        renderer: (data) => (
          <span style={{ marginRight: '60px' }}>{Math.floor(getLicenseActiveHours(data.fromDate, data.untilDate, data.deviceType) / 24)}</span>
        ),
      },
      {
        sortable: true,
        filter: <DateRangeFilter />,
        field: 'createdOn',
        fixed: 'none',
        title: createdOnHeader,
        renderer: (data) => <span>{moment(data.createdOn).local().format(getDateFormat())}</span>,
      },
      {
        sortable: true,
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={licenseDataStore.baseUrl} fieldName={'createdBy'} fieldValue={'createdBy'} />,
        field: 'createdBy',
        fixed: 'none',
        title: createdByHeader,
        renderer: (data) => <span>{data.createdBy}</span>,
      },
      {
        sortable: true,
        field: 'customer',
        title: customerHeader,
        fixed: 'none',
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={licenseDataStore.baseUrl} fieldName={'customer'} fieldValue={'customer'} />,
        renderer: (data) => <span>{data.customer}</span>,
      },
      {
        sortable: true,
        field: 'project',
        title: projectHeader,
        fixed: 'none',
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={licenseDataStore.baseUrl} fieldName={'project'} fieldValue={'project'} />,
        renderer: (data) => <span>{data.project}</span>,
      },
      {
        sortable: false,
        title: organizationNameHeader,
        filter: (
          <OptionsFilter
            allowSearch
            options={organizationsState.items.value.filter((v) => v.isPartner).map((v) => ({ label: v.displayName, value: v.id }))}
          />
        ),
        fiterField: 'organizationName',
        field: 'organizationName',
        queryFilterMode: CustomFilterField.UseCustomQueryFilter,
        customQueryFilter: organizationFilter,
        filtered: filteredByOrganization,
        renderer: (data) => (
          <span>{t(organizationsState.items.value.filter((v) => v.id === data.organizationId).firstOrDefault()?.displayName ?? '')}</span>
        ),
      },
      {
        sortable: false,
        title: t('Country'),
        filter: (
          <OptionsFilter
            allowSearch
            options={Object.values(Country).flatMap((country) =>
              organizationsState.items.value.find((v) => v.country === country) ? [{ label: t(country), value: country }] : []
            )}
          />
        ),
        fiterField: 'country',
        field: 'country',
        queryFilterMode: CustomFilterField.UseCustomQueryFilter,
        filtered: filteredByCountry,
        customQueryFilter: oganizationCountryFilter,
        renderer: (data) => (
          <span>{t(organizationsState.items.value.filter((v) => v.id === data.organizationId).firstOrDefault()?.country ?? '')}</span>
        ),
      },
      {
        field: 'status',
        title: t('Status'),
        fiterField: 'status',
        queryFilterMode: CustomFilterField.UseCustomQueryFilter,
        customQueryFilter: statusFilter,
        filter: (
          <OptionsFilter
            options={[
              { value: 'Active', label: t('Active') },
              { value: 'NotActive', label: t('NotActive') },
              { value: 'Pending', label: t('ApprovalPending') },
            ]}
          />
        ),
        fixed: 'none',
        renderer: getActivationStatusLabel,
      },
      {
        field: 'isRecurrent',
        title: t('Recurrence'),
        fiterField: 'isRecurrent',
        filter: (
          <OptionsFilter
            options={[
              { value: true, label: t('Activated') },
              { value: false, label: t('Deactivated') },
            ]}
          />
        ),
        fixed: 'none',
        renderer: (license: LicenseDataSummaryItem) => {
          let color = '';
          let text = '';

          if (license?.isRecurrent) {
            color = token.colorSuccess;
            text = t('Activated');
          } else {
            color = token.colorError;
            text = t('Deactivated');
          }

          return <span style={{ color }}>{text}</span>;
        },
      },
      {
        sortable: true,
        field: 'signOffPending',
        title: t('SignOffPending'),
        fixed: 'none',
        renderer: (data) => <span>{data.signOffPending ? t('Yes') : t('No')}</span>,
      },
      {
        sortable: false,
        searchable: false,
        fixed: onMobile ? 'none' : 'right',
        field: '',
        title: t(''),
        renderer: (data) => (
          <span
            className="clickable-span"
            onClick={() => {
              setSelectedLicense(data.id);
              setLicenseVisible(true);
            }}
          >
            {t('ShowLicense')}
          </span>
        ),
      },
    ],
    data: { ...licenseDataState.value, isBusy: licenseDataState.value.isBusy },
  } as TableModel<LicenseDataSummaryItem>;

  return (
    <Spin spinning={licenseDataState.value.isBusy}>
      <Row align="top" justify="space-between" className="table-overflow">
        <Card className="card-table licenses-table" style={{ width: '100%' }}>
          <Row style={{ width: '100%', padding: '50px 10px 30px 20px' }} gutter={[10, 10]} justify={'space-between'}>
            <Col xs={{ span: 24, order: 2 }} sm={{ span: 24, order: 2 }} md={{ span: 24, order: 2 }} lg={{ span: 12, order: 1 }}>
              <Select
                disabled={licenseDataState.count.value === 0 && (selectedCustomDate === undefined || isEqual(selectedCustomDate, [null, null]))}
                popupClassName="license-select-date-range-dropdown"
                value={selectedDateRangeFilterValue}
                allowClear
                onClear={onClearActiveRangeFilter}
                style={{ width: '100%' }}
                placeholder={t('FilterByActiveDates')}
                open={selectFilterOpen}
                onDropdownVisibleChange={(visible) => setSelectFilterOpen(visible)}
                dropdownRender={() => (
                  <>
                    <div
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <Divider style={{ margin: '8px 0' }} />
                      <Space style={{ padding: '0 8px 4px' }}>
                        <RangePicker
                          classNames={{
                            popup: {
                              root: 'responsive-range-picker',
                            },
                          }}
                          value={selectedCustomDate}
                          onChange={setCustomFilter}
                          allowClear={false}
                        />
                      </Space>
                      <Button
                        type="primary"
                        style={{ width: 'calc(100% - 20px)', height: '40px', margin: '10px' }}
                        disabled={!selectedDateRangeFilterValue}
                        onClick={onClearActiveRangeFilter}
                      >
                        {t('ClearFilter')}
                      </Button>
                    </div>
                  </>
                )}
              />
            </Col>

            <Col xs={{ span: 24, order: 1 }} sm={{ span: 24, order: 1 }} md={{ span: 24, order: 1 }} lg={{ span: 12, order: 2 }}>
              <Row gutter={[10, 10]} justify={'end'}>
                <Col>
                  <Tooltip placement="topLeft" title={t('Export')}>
                    <Button type="primary" onClick={() => setReportsModal(true)}>
                      {t('ExportCSV')}
                    </Button>
                  </Tooltip>
                </Col>

                <Col>
                  <Tooltip placement="topLeft" title={t('ViewLogs')}>
                    <Button type="primary" onClick={() => navigate(`logs`)}>
                      {t('ViewLogs')}
                    </Button>
                  </Tooltip>
                </Col>

                {((Object.keys(query?.filter ?? {}).length !== 0 && Object.values(query?.filter ?? {}).any((v) => v !== undefined && v !== null)) ||
                  (selectedCustomDate && selectedCustomDate[0] !== null && selectedCustomDate[1] !== null) ||
                  filteredByCountry ||
                  filteredByOrganization ||
                  (organizationIdsFilter.current?.length !== undefined && organizationIdsFilter.current?.length > 0)) && (
                  <Col>
                    <Tooltip placement="topLeft" title={t('ClearFilters')}>
                      <Button
                        type="primary"
                        onClick={() => {
                          organizationIdsFilter.current = [];
                          setFilteredByCountry(false);
                          setFilteredByOrganization(false);
                          setSelectedDateRangeFilterValue(undefined);
                          setSelectFilterOpen(false);
                          activeDatesFilter.current = undefined;
                          labelRef.current = undefined;
                          setSelectedCustomDate([null, null]);
                          handleQueryChanged(initialQuery);
                        }}
                      >
                        {t('ClearFilters')}
                      </Button>
                    </Tooltip>
                  </Col>
                )}

                <Col>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewLicenseVisible(true)}>
                    {t('AddLicense')}
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="default"
                    onClick={() => {
                      licenseDataStore.loadFromOrganizationIds(query, organizationIdsFilter.current);
                    }}
                  >
                    <ReloadOutlined style={{ marginRight: 4, marginLeft: 4 }} />
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <TableView
            hideFilter={true}
            rowKey="id"
            hideRefresh
            canCreateNew={false}
            newItemName={t('AddLicense')}
            canEdit={false}
            canSort
            onQueryChanged={handleQueryChanged}
            model={licenseDataState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              licenseDataState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(licenseDataState.errorMessage.value) : t('LicensesTableError'))
            }
          />
          {newLicenseVisible && (
            <NewLicenseDialog
              isGlobalLicenseManager={true}
              attributes={licenseCreationAttributesState.item.value as LicenseCreationAttributes}
              onClose={(response: any) => {
                setNewLicenseVisible(false);
                if (response) {
                  licenseDataStore.loadFromOrganizationIds(query, organizationIdsFilter.current);
                  setSelectedLicense(response.identity);
                  setLicenseVisible(true);
                  //history.push(`${match.url}/${response.identity}`);
                }
              }}
            />
          )}
          {licenseVisible && selectedLicense && (
            <ShowLicenseDialog
              licenseDataId={selectedLicense}
              onClose={() => {
                setLicenseVisible(false);
              }}
            />
          )}
        </Card>
      </Row>
      {reportsModal && (
        <Modal
          title={<h3>{`${t('Reports')}`}</h3>}
          open={reportsModal}
          onOk={handleExportOk}
          onCancel={handleExportCancel}
          okText={t('Export')}
          cancelText={t('Cancel')}
          cancelButtonProps={{ type: 'default' }}
        >
          <CsvReport onChange={onChangeItem} />
        </Modal>
      )}
    </Spin>
  );
};

export default LicenseListPage;
